// web/src/screens/NewRequest.tsx (/request) — "Ask for a change" (portal spec 19, G7): one text box and optional photos.
// The request is stored for the update agent, which makes the change on a preview first; the client taps "Make it live"
// on the request when they're happy. Photos go through the upload link (PhotoPicker) and ride in the request body as
// `Uploaded image: <address>` lines. Changes open up once a site is live (the API 409s otherwise), so the picker only
// offers live sites.
// With VITE_CHALICE_ENABLED (the hidden engine, spec 10) the old media uploader and the credit note come back.
import { useCallback, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { FEATURES } from '../lib/features';
import { apiErrorText } from '../lib/errors';
import { PageHeader } from '../components/PageHeader';
import { FormField } from '../components/FormField';
import { Icon } from '../components/Icon';
import { MediaUpload } from '../components/MediaUpload';
import { PhotoPicker } from '../components/PhotoPicker';
import { toast } from '../components/Toast';

const CHANGE_STAGES = ['live', 'legacy'];

/** A short title for lists: the first line of what they wrote. */
function titleFrom(text: string): string {
  const first = text.trim().split(/\r?\n/)[0]!.trim();
  return first.length > 80 ? `${first.slice(0, 77).trimEnd()}…` : first;
}

export function NewRequest() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.listSites() });
  // Credits only meter the hidden chalice engine (spec 15 §2b); the update agent's work isn't counted in credits.
  const credits = useQuery({ queryKey: ['credits'], queryFn: () => api.getCredits(), enabled: FEATURES.chalice });
  const [siteId, setSiteId] = useState(params.get('site') ?? '');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState<string[]>([]);
  const [assetIds, setAssetIds] = useState<string[]>([]);
  const [mediaBusy, setMediaBusy] = useState(false);
  const onPhotos = useCallback((urls: string[], uploading: boolean) => { setPhotos(urls); setMediaBusy(uploading); }, []);
  const onMediaChange = useCallback((ids: string[], uploading: boolean) => { setAssetIds(ids); setMediaBusy(uploading); }, []);

  const live = (sites.data ?? []).filter((s) => CHANGE_STAGES.includes(s.stage));
  const effectiveSite = live.find((s) => s.id === siteId)?.id ?? live[0]?.id ?? '';

  const c = FEATURES.chalice ? credits.data : undefined;
  const left = c ? Math.max(c.creditsGranted - c.creditsUsed, 0) : 0;
  const creditNote = !c
    ? null
    : c.killed
      ? 'This account is paused — please contact us and we’ll get it sorted before this update starts.'
      : left <= 0
        ? 'You’re out of credits for this month. You can still send this — it’ll be held, and it starts the moment you add credits or your month resets.'
        : c.creditsGranted > 0 && left / c.creditsGranted < 0.2
          ? `You have ${left.toLocaleString()} credits left this month.`
          : null;

  async function submit() {
    const text = body.trim();
    if (!effectiveSite || !text) return;
    setBusy(true);
    try {
      const lines = FEATURES.chalice ? [] : photos.map((u) => `Uploaded image: ${u}`);
      const fullBody = [text, ...(lines.length ? ['', ...lines] : [])].join('\n');
      const { id } = await api.submitRequest(effectiveSite, titleFrom(text), fullBody, 'change', FEATURES.chalice ? assetIds : undefined);
      qc.invalidateQueries({ queryKey: ['site', effectiveSite] });
      qc.invalidateQueries({ queryKey: ['requests'] });
      qc.invalidateQueries({ queryKey: ['home'] });
      toast({ kind: 'success', message: "Got it. We'll show you a preview before anything goes live." });
      nav(`/requests/${id}`);
    } catch (e) {
      const m = apiErrorText(e, '');
      toast({
        kind: 'error',
        message: m.includes('not updatable')
          ? "This site isn't set up for changes yet — contact us and we'll sort it."
          : /changes open up/.test(m)
            ? 'Changes open up once your site is live.'
            // The media refusals (doc 30 §8) already arrive as customer-safe sentences.
            : /still uploading|no longer available|files to one update/.test(m)
              ? m
              : "Couldn't send that — please try again.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button onClick={() => nav(-1)} className="mb-4 inline-flex min-h-11 items-center gap-1 text-sm text-muted hover:text-ink"><Icon name="arrow-left" size={16} />Back</button>
      <PageHeader title="Ask for a change" subtitle="Tell us what you'd like changed. We'll show you a preview before anything goes live." />
      {sites.isSuccess && live.length === 0 ? (
        <div className="max-w-xl rounded-lg border border-hair bg-surface p-4">
          <p className="text-sm text-ink">Changes open up once your site is live. Until then, you can send us photos you'd like used.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/photo" className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink"><Icon name="image" size={16} />Send photos</Link>
            <Link to="/" className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink">Home</Link>
          </div>
        </div>
      ) : (
        <div className="flex max-w-xl flex-col gap-5">
          {live.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="req-site" className="text-sm font-medium text-ink">Which site is this for?</label>
              <select id="req-site" value={effectiveSite} onChange={(e) => setSiteId(e.target.value)}
                className="min-h-11 w-full rounded-md border border-hair bg-bg px-3 py-2.5 text-ink">
                {live.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <FormField label="What would you like changed?" name="req-body" required textarea rows={6} value={body} onChange={setBody}
            helper="Plain words are perfect, e.g. “Change the phone number to 801-555-0100.” If anything's unclear we'll ask here." />
          {effectiveSite && (FEATURES.chalice
            ? <MediaUpload key={effectiveSite} siteId={effectiveSite} onChange={onMediaChange} disabled={busy} />
            : <PhotoPicker key={effectiveSite} onChange={onPhotos} disabled={busy} />)}
          {creditNote && (
            <p className="rounded-md border border-hair border-l-4 border-l-warn bg-surface px-3 py-2.5 text-sm text-ink">
              {creditNote}
              {c && !c.killed && left <= 0 && (<> <Link to="/billing" className="text-info underline">Add credits</Link>.</>)}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <button onClick={submit} disabled={busy || mediaBusy || !effectiveSite || !body.trim()}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98] disabled:opacity-50">
              <Icon name="send" size={16} />{busy ? 'Sending…' : 'Send'}
            </button>
            {mediaBusy && !busy && (
              <p className="text-xs text-muted">Waiting for your photos to finish uploading.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
